import { IconButton, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { StyledMenu } from "../ui";
import { RecommendedReasonHeader } from "./RecommendedReasonHeader";
import { RecommendedAccountActionBlock } from "./RecommendedAccountActionBlock";
import {
  ArrowsExpandIcon,
  GeoIcon,
  IndustryIcon,
  RevenueIcon,
} from "../images";
import { RecommendedAccount } from "../../types/apiTypes";
import CloseIcon from "@mui/icons-material/Close";
import { TwoPersonIcon } from "../images/TwoPersonIcon";

type Props = {
  recommendedAccount: RecommendedAccount;
};

export const RecommendedCardDetails = ({ recommendedAccount }: Props) => {
  const { id, region, reason, size, revenue, name, industry } =
    recommendedAccount;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        disableRipple
        onClick={handleClick}
        sx={{
          padding: 0,
          borderRadius: "6px",
        }}
      >
        <ArrowsExpandIcon />
      </IconButton>

      <StyledMenu
        open={open}
        onClose={handleClose}
        anchorEl={null}
        anchorReference="anchorPosition"
        anchorPosition={{
          top: window.innerHeight / 2,
          left: window.innerWidth / 2,
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#F7F7F7",
            padding: "16px",
            borderRadius: "8px",
            border: "1px solid #DEDEDE",
            maxWidth: "920px",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(255,255,255,0.6)",
          },
        }}
      >
        <Stack gap={"16px"}>
          <Stack
            sx={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              paddingLeft: "6px",
            }}
          >
            <Typography
              variant={"subtitle1"}
              sx={{ color: "#232323", lineHeight: "22px", fontWeight: 600 }}
            >
              {name}
            </Typography>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{ ml: "auto", color: "#828282" }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          <Stack flexDirection={"row"} gap={"8px"}>
            <Stack
              sx={{
                backgroundColor: "#FFFFFF",
                paddingY: "16px",
                borderRadius: "8px",
                border: "1px solid #DEDEDE",
                flexGrow: 1,
                maxWidth: "240px",
                width: "100%",
                gap: "16px",
              }}
            >
              <Stack
                flexDirection={"row"}
                gap={"8px"}
                alignItems={"flex-start"}
                sx={{
                  padding: "0 16px 0 12px",
                }}
              >
                <Stack
                  sx={{
                    width: "22px",
                    height: "22px",
                    flexShrink: 0,
                    border: "1px solid #DEDEDE",
                    borderRadius: "50%",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <GeoIcon />
                </Stack>
                <Typography
                  variant={"body2"}
                  sx={{
                    color: "#232323",
                    fontWeight: 500,
                  }}
                >
                  {region}
                </Typography>
              </Stack>
              <Stack gap={"12px"} paddingX={"16px"}>
                <Stack
                  flexDirection={"row"}
                  gap={"8px"}
                  alignItems={"flex-start"}
                >
                  <TwoPersonIcon
                    color={"#797979"}
                    secondColor={"#797979"}
                    width={"14px"}
                    height={"14px"}
                  />
                  <Typography
                    variant={"body2"}
                    sx={{
                      color: "#232323",
                      fontWeight: 500,
                    }}
                  >
                    {size} Employees
                  </Typography>
                </Stack>
                <Stack
                  flexDirection={"row"}
                  gap={"8px"}
                  alignItems={"flex-start"}
                >
                  <IndustryIcon />
                  <Typography
                    variant={"body2"}
                    sx={{
                      color: "#232323",
                      fontWeight: 500,
                    }}
                  >
                    {industry}
                  </Typography>
                </Stack>
                <Stack
                  flexDirection={"row"}
                  gap={"8px"}
                  alignItems={"flex-start"}
                >
                  <RevenueIcon />
                  <Typography
                    variant={"body2"}
                    sx={{
                      color: "#232323",
                      fontWeight: 500,
                    }}
                  >
                    {revenue || "--"}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
            <Stack
              sx={{
                backgroundColor: "#FFFFFF",
                padding: "16px",
                borderRadius: "8px",
                border: "1px solid #DEDEDE",
                flexGrow: 1,
                width: "100%",
                gap: "12px",
                justifyContent: "space-between",
              }}
            >
              <Stack gap={"12px"}>
                <RecommendedReasonHeader />
                <Typography variant={"body1"} sx={{ color: "#464646" }}>
                  {reason}
                </Typography>
              </Stack>
              <Stack sx={{ maxWidth: "256px", alignSelf: "flex-end" }}>
                <RecommendedAccountActionBlock ids={[id]} />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </StyledMenu>
    </>
  );
};
